/**
 * GroupeBloc — Affiche les groupes familiaux/relationnels d'un client
 * et permet de créer/rejoindre un groupe, ajouter des membres, modifier les rôles.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../utils/api.js';
import { colors } from '../../styles/tokens.js';
import type { Groupe, GroupeType, Client } from '../../types/clients.js';

interface GroupeBlocProps {
  clientId: number;
  clientNom: string;
  clientPrenom: string;
}

const TYPE_LABELS: Record<GroupeType, { label: string; icon: string; color: string }> = {
  FAMILLE: { label: 'Famille', icon: 'bi-people-fill', color: '#2563eb' },
  COUPLE: { label: 'Couple', icon: 'bi-heart-fill', color: '#dc2626' },
  AUTRE: { label: 'Autre', icon: 'bi-diagram-3-fill', color: '#7c3aed' },
};

export function GroupeBloc({ clientId, clientNom, clientPrenom }: GroupeBlocProps) {
  const [groupes, setGroupes] = useState<Groupe[]>([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [showAddMember, setShowAddMember] = useState<number | null>(null); // groupeId

  useEffect(() => {
    loadGroupes();
  }, [clientId]);

  async function loadGroupes() {
    try {
      const res = await apiFetch<Groupe[]>(`/clients/${clientId}/groupes`);
      if (res.success && res.data) setGroupes(res.data);
    } catch { /* silencieux */ }
    finally { setLoading(false); }
  }

  async function handleDeleteGroupe(groupeId: number) {
    if (!confirm('Supprimer ce groupe ? Les clients ne seront pas supprimés.')) return;
    try {
      const res = await apiFetch(`/groupes/${groupeId}`, { method: 'DELETE' });
      if (res.success) setGroupes((prev) => prev.filter((g) => g.id !== groupeId));
    } catch { /* silencieux */ }
  }

  async function handleRemoveMember(groupeId: number, memberId: number) {
    if (!confirm('Retirer ce membre du groupe ?')) return;
    try {
      const res = await apiFetch(`/groupes/${groupeId}/membres/${memberId}`, { method: 'DELETE' });
      if (res.success) {
        // Si on se retire soi-même, retirer le groupe de la liste
        if (memberId === clientId) {
          setGroupes((prev) => prev.filter((g) => g.id !== groupeId));
        } else {
          setGroupes((prev) => prev.map((g) =>
            g.id === groupeId
              ? { ...g, membres: g.membres.filter((m) => m.clientId !== memberId) }
              : g
          ));
        }
      }
    } catch { /* silencieux */ }
  }

  const sideCard: React.CSSProperties = {
    background: '#fff',
    borderRadius: 8,
    padding: '1.5rem',
    marginBottom: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  };

  if (loading) return null;

  return (
    <>
      <div style={sideCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ color: colors.text, margin: 0, fontSize: '1rem' }}>
            <i className="bi bi-people" style={{ color: '#2563eb', marginRight: 8 }} />
            Liens & Groupes
          </h3>
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              onClick={() => setShowJoin(true)}
              style={{
                border: '1px solid #2563eb', background: 'transparent', color: '#2563eb',
                borderRadius: 6, padding: '4px 10px', fontSize: '0.75rem', cursor: 'pointer',
                fontFamily: 'inherit',
              }}
              title="Rejoindre un groupe existant"
            >
              Rejoindre
            </button>
            <button
              onClick={() => setShowCreate(true)}
              style={{
                width: 28, height: 28, borderRadius: '50%',
                background: '#2563eb', color: '#fff', border: 'none',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1rem', fontWeight: 700, lineHeight: 1, cursor: 'pointer',
              }}
              title="Créer un groupe"
            >
              +
            </button>
          </div>
        </div>

        {groupes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '0.75rem', color: colors.textMuted, fontSize: '0.85rem' }}>
            <i className="bi bi-people" /> Aucun groupe
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {groupes.map((groupe) => {
              const typeInfo = TYPE_LABELS[groupe.type];
              return (
                <div key={groupe.id} style={{ background: colors.bgPage, borderRadius: 8, padding: '0.75rem' }}>
                  {/* Header du groupe */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <i className={`bi ${typeInfo.icon}`} style={{ color: typeInfo.color, fontSize: '0.9rem' }} />
                    <span style={{ fontWeight: 600, fontSize: '0.9rem', flex: 1 }}>{groupe.nom}</span>
                    <span style={{
                      fontSize: '0.7rem', padding: '2px 8px', borderRadius: 12,
                      background: typeInfo.color + '15', color: typeInfo.color,
                    }}>
                      {typeInfo.label}
                    </span>
                    <button
                      onClick={() => setShowAddMember(groupe.id)}
                      style={{
                        border: 'none', background: 'none', cursor: 'pointer',
                        color: '#2563eb', fontSize: '0.8rem', padding: 0,
                      }}
                      title="Ajouter un membre"
                    >
                      <i className="bi bi-person-plus" />
                    </button>
                    <i
                      className="bi bi-trash"
                      onClick={() => handleDeleteGroupe(groupe.id)}
                      style={{ color: colors.danger, cursor: 'pointer', fontSize: '0.75rem' }}
                      title="Supprimer le groupe"
                    />
                  </div>

                  {/* Liste des membres */}
                  <div style={{ display: 'grid', gap: 4 }}>
                    {groupe.membres.map((m) => {
                      const isSelf = m.clientId === clientId;
                      return (
                        <div key={m.clientId} style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          fontSize: '0.85rem', padding: '2px 0',
                        }}>
                          <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}>•</span>
                          {isSelf ? (
                            <span style={{ flex: 1, fontWeight: 500 }}>
                              {m.client.prenom} {m.client.nom}
                              <span style={{ color: colors.textMuted, fontStyle: 'italic', marginLeft: 4 }}>(vous)</span>
                            </span>
                          ) : (
                            <Link
                              to={`/clients/${m.clientId}`}
                              style={{ flex: 1, color: colors.text, textDecoration: 'none', fontWeight: 500 }}
                            >
                              {m.client.prenom} {m.client.nom}
                              <i className="bi bi-arrow-right" style={{ marginLeft: 4, fontSize: '0.7rem', color: colors.accent }} />
                            </Link>
                          )}
                          {m.role && (
                            <span style={{
                              fontSize: '0.7rem', color: colors.textMid, fontStyle: 'italic',
                            }}>
                              {m.role}
                            </span>
                          )}
                          {!isSelf && (
                            <i
                              className="bi bi-x"
                              onClick={() => handleRemoveMember(groupe.id, m.clientId)}
                              style={{ color: colors.textMuted, cursor: 'pointer', fontSize: '0.8rem' }}
                              title="Retirer du groupe"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreate && (
        <CreateGroupeModal
          clientId={clientId}
          clientNom={clientNom}
          clientPrenom={clientPrenom}
          onClose={() => setShowCreate(false)}
          onCreated={(g) => { setGroupes((prev) => [...prev, g]); setShowCreate(false); }}
        />
      )}

      {showJoin && (
        <JoinGroupeModal
          clientId={clientId}
          existingGroupeIds={groupes.map((g) => g.id)}
          onClose={() => setShowJoin(false)}
          onJoined={() => { loadGroupes(); setShowJoin(false); }}
        />
      )}

      {showAddMember !== null && (
        <AddMembreModal
          groupeId={showAddMember}
          existingClientIds={groupes.find((g) => g.id === showAddMember)?.membres.map((m) => m.clientId) ?? []}
          onClose={() => setShowAddMember(null)}
          onAdded={(updated) => {
            setGroupes((prev) => prev.map((g) => g.id === updated.id ? { ...updated, monRole: g.monRole } : g));
            setShowAddMember(null);
          }}
        />
      )}
    </>
  );
}

// ─── Modal overlay commun ───

function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.4)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: '1rem',
      }}
    >
      <div style={{
        background: '#fff', borderRadius: 12, padding: '1.5rem',
        width: '100%', maxWidth: 480, maxHeight: '80vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        {children}
      </div>
    </div>
  );
}

// ─── Composant recherche client réutilisable ───

function ClientSearch({ excludeIds, onSelect, placeholder }: {
  excludeIds: number[];
  onSelect: (client: Client) => void;
  placeholder?: string;
}) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    const timer = setTimeout(() => {
      setLoading(true);
      apiFetch<{ clients: Client[] }>(`/clients?search=${encodeURIComponent(search)}&limit=10`)
        .then((res) => {
          if (res.success && res.data) {
            setResults(res.data.clients.filter((c) => !excludeIds.includes(c.id)));
          }
        })
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [search, excludeIds]);

  return (
    <div>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={placeholder ?? 'Rechercher un client (nom ou prénom)...'}
        style={{
          width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`,
          borderRadius: 6, fontSize: '0.9rem', fontFamily: 'inherit', boxSizing: 'border-box',
        }}
      />
      {loading && <p style={{ color: colors.textMuted, fontSize: '0.8rem', margin: '4px 0' }}>Recherche...</p>}
      {results.length > 0 && (
        <div style={{
          maxHeight: 160, overflowY: 'auto',
          border: `1px solid ${colors.border}`, borderRadius: 6, marginTop: 4,
        }}>
          {results.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => { onSelect(c); setSearch(''); setResults([]); }}
              style={{
                display: 'block', width: '100%', padding: '8px 12px',
                border: 'none', background: 'transparent', cursor: 'pointer',
                textAlign: 'left', fontSize: '0.85rem', fontFamily: 'inherit',
                borderBottom: `1px solid ${colors.bgPage}`,
              }}
            >
              <strong>{c.prenom} {c.nom}</strong>
              {c.lieuNaissance && <span style={{ color: colors.textMuted, marginLeft: 6 }}>({c.lieuNaissance})</span>}
            </button>
          ))}
        </div>
      )}
      {search.length >= 2 && !loading && results.length === 0 && (
        <p style={{ color: colors.textMuted, fontSize: '0.8rem', textAlign: 'center', margin: '4px 0' }}>
          Aucun client trouvé
        </p>
      )}
    </div>
  );
}

// ─── Modal Créer un groupe ───

interface PendingMembre {
  clientId: number;
  prenom: string;
  nom: string;
  role: string;
}

function CreateGroupeModal({ clientId, clientNom, clientPrenom, onClose, onCreated }: {
  clientId: number;
  clientNom: string;
  clientPrenom: string;
  onClose: () => void;
  onCreated: (g: Groupe) => void;
}) {
  const [nom, setNom] = useState('');
  const [type, setType] = useState<GroupeType>('FAMILLE');
  const [role, setRole] = useState('');
  const [saving, setSaving] = useState(false);

  // Membres additionnels (en plus du client courant)
  const [extraMembres, setExtraMembres] = useState<PendingMembre[]>([]);

  function handleAddExtra(client: Client) {
    if (client.id === clientId) return;
    if (extraMembres.some((m) => m.clientId === client.id)) return;
    setExtraMembres((prev) => [...prev, {
      clientId: client.id, prenom: client.prenom, nom: client.nom, role: '',
    }]);
  }

  function handleExtraRole(cid: number, newRole: string) {
    setExtraMembres((prev) => prev.map((m) => m.clientId === cid ? { ...m, role: newRole } : m));
  }

  function handleRemoveExtra(cid: number) {
    setExtraMembres((prev) => prev.filter((m) => m.clientId !== cid));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!nom.trim()) return;
    setSaving(true);
    try {
      const membres = [
        { clientId, role: role.trim() || null },
        ...extraMembres.map((m) => ({ clientId: m.clientId, role: m.role.trim() || null })),
      ];
      const res = await apiFetch<Groupe>('/groupes', {
        method: 'POST',
        body: JSON.stringify({ nom: nom.trim(), type, membres }),
      });
      if (res.success && res.data) {
        onCreated({ ...res.data, monRole: role.trim() || null });
      }
    } catch { /* silencieux */ }
    finally { setSaving(false); }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`,
    borderRadius: 6, fontSize: '0.9rem', fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  const excludeIds = [clientId, ...extraMembres.map((m) => m.clientId)];

  return (
    <ModalOverlay onClose={onClose}>
      <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem' }}>
        <i className="bi bi-plus-circle" style={{ color: '#2563eb', marginRight: 8 }} />
        Créer un groupe
      </h3>
      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '0.75rem' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', color: colors.textMid, marginBottom: 4 }}>
            Nom du groupe *
          </label>
          <input
            type="text"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            placeholder="ex. Famille Martin"
            style={inputStyle}
            autoFocus
            required
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', color: colors.textMid, marginBottom: 4 }}>
            Type
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['FAMILLE', 'COUPLE', 'AUTRE'] as const).map((t) => {
              const info = TYPE_LABELS[t];
              const selected = type === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  style={{
                    flex: 1, padding: '8px', borderRadius: 6, border: `2px solid ${selected ? info.color : colors.border}`,
                    background: selected ? info.color + '10' : 'transparent',
                    color: selected ? info.color : colors.textMid,
                    cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'inherit',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  }}
                >
                  <i className={`bi ${info.icon}`} />
                  {info.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Client courant */}
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', color: colors.textMid, marginBottom: 4 }}>
            Votre rôle ({clientPrenom} {clientNom})
          </label>
          <input
            type="text"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="ex. mère, père, enfant, partenaire..."
            style={inputStyle}
          />
        </div>

        {/* Membres additionnels */}
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', color: colors.textMid, marginBottom: 4 }}>
            Ajouter d'autres membres
          </label>
          <ClientSearch excludeIds={excludeIds} onSelect={handleAddExtra} />

          {extraMembres.length > 0 && (
            <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
              {extraMembres.map((m) => (
                <div key={m.clientId} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 8px', background: colors.bgPage, borderRadius: 6,
                }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {m.prenom} {m.nom}
                  </span>
                  <input
                    type="text"
                    value={m.role}
                    onChange={(e) => handleExtraRole(m.clientId, e.target.value)}
                    placeholder="rôle..."
                    style={{
                      flex: 1, padding: '4px 8px', border: `1px solid ${colors.border}`,
                      borderRadius: 4, fontSize: '0.8rem', fontFamily: 'inherit',
                      minWidth: 0,
                    }}
                  />
                  <i
                    className="bi bi-x-lg"
                    onClick={() => handleRemoveExtra(m.clientId)}
                    style={{ color: colors.danger, cursor: 'pointer', fontSize: '0.75rem', flexShrink: 0 }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 16px', borderRadius: 6, border: `1px solid ${colors.border}`,
              background: 'transparent', color: colors.textMid, cursor: 'pointer',
              fontSize: '0.85rem', fontFamily: 'inherit',
            }}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving || !nom.trim()}
            style={{
              padding: '8px 16px', borderRadius: 6, border: 'none',
              background: '#2563eb', color: '#fff', cursor: saving ? 'wait' : 'pointer',
              fontSize: '0.85rem', fontFamily: 'inherit', fontWeight: 600,
              opacity: saving || !nom.trim() ? 0.5 : 1,
            }}
          >
            {saving ? 'Création...' : 'Créer le groupe'}
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ─── Modal Rejoindre un groupe existant ───

function JoinGroupeModal({ clientId, existingGroupeIds, onClose, onJoined }: {
  clientId: number;
  existingGroupeIds: number[];
  onClose: () => void;
  onJoined: () => void;
}) {
  const [allGroupes, setAllGroupes] = useState<Groupe[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroupeId, setSelectedGroupeId] = useState<number | null>(null);
  const [role, setRole] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiFetch<Groupe[]>('/groupes')
      .then((res) => {
        if (res.success && res.data) {
          // Exclure les groupes déjà rejoints
          setAllGroupes(res.data.filter((g) => !existingGroupeIds.includes(g.id)));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedGroupeId) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/groupes/${selectedGroupeId}/membres`, {
        method: 'POST',
        body: JSON.stringify({ clientId, role: role.trim() || null }),
      });
      if (res.success) onJoined();
    } catch { /* silencieux */ }
    finally { setSaving(false); }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`,
    borderRadius: 6, fontSize: '0.9rem', fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  return (
    <ModalOverlay onClose={onClose}>
      <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem' }}>
        <i className="bi bi-box-arrow-in-right" style={{ color: '#2563eb', marginRight: 8 }} />
        Rejoindre un groupe
      </h3>
      {loading ? (
        <p style={{ color: colors.textMuted, fontSize: '0.85rem' }}>Chargement des groupes...</p>
      ) : allGroupes.length === 0 ? (
        <p style={{ color: colors.textMuted, fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0' }}>
          Aucun groupe disponible. Créez-en un d'abord.
        </p>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '0.75rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', color: colors.textMid, marginBottom: 4 }}>
              Groupe
            </label>
            <select
              value={selectedGroupeId ?? ''}
              onChange={(e) => setSelectedGroupeId(Number(e.target.value) || null)}
              style={inputStyle}
            >
              <option value="">— Sélectionner un groupe —</option>
              {allGroupes.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.nom} ({TYPE_LABELS[g.type].label} — {g.membres.length} membres)
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', color: colors.textMid, marginBottom: 4 }}>
              Rôle dans ce groupe
            </label>
            <input
              type="text"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="ex. mère, fils, partenaire..."
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              type="button" onClick={onClose}
              style={{
                padding: '8px 16px', borderRadius: 6, border: `1px solid ${colors.border}`,
                background: 'transparent', color: colors.textMid, cursor: 'pointer',
                fontSize: '0.85rem', fontFamily: 'inherit',
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving || !selectedGroupeId}
              style={{
                padding: '8px 16px', borderRadius: 6, border: 'none',
                background: '#2563eb', color: '#fff', cursor: saving ? 'wait' : 'pointer',
                fontSize: '0.85rem', fontFamily: 'inherit', fontWeight: 600,
                opacity: saving || !selectedGroupeId ? 0.5 : 1,
              }}
            >
              {saving ? 'Ajout...' : 'Rejoindre'}
            </button>
          </div>
        </form>
      )}
    </ModalOverlay>
  );
}

// ─── Modal Ajouter un membre ───

function AddMembreModal({ groupeId, existingClientIds, onClose, onAdded }: {
  groupeId: number;
  existingClientIds: number[];
  onClose: () => void;
  onAdded: (g: Groupe) => void;
}) {
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [role, setRole] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleAdd() {
    if (!selectedClient) return;
    setSaving(true);
    try {
      const res = await apiFetch<Groupe>(`/groupes/${groupeId}/membres`, {
        method: 'POST',
        body: JSON.stringify({ clientId: selectedClient.id, role: role.trim() || null }),
      });
      if (res.success && res.data) onAdded(res.data);
    } catch { /* silencieux */ }
    finally { setSaving(false); }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem' }}>
        <i className="bi bi-person-plus" style={{ color: '#2563eb', marginRight: 8 }} />
        Ajouter un membre
      </h3>

      {!selectedClient ? (
        <ClientSearch
          excludeIds={existingClientIds}
          onSelect={setSelectedClient}
        />
      ) : (
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          <div style={{
            padding: '0.75rem', background: colors.bgPage, borderRadius: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
              {selectedClient.prenom} {selectedClient.nom}
            </span>
            <button
              type="button"
              onClick={() => setSelectedClient(null)}
              style={{
                border: 'none', background: 'none', cursor: 'pointer',
                color: colors.textMuted, fontSize: '0.8rem', fontFamily: 'inherit',
              }}
            >
              Changer
            </button>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', color: colors.textMid, marginBottom: 4 }}>
              Rôle dans le groupe
            </label>
            <input
              type="text"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="ex. fille, frère, partenaire..."
              style={{
                width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`,
                borderRadius: 6, fontSize: '0.9rem', fontFamily: 'inherit', boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              type="button" onClick={onClose}
              style={{
                padding: '8px 16px', borderRadius: 6, border: `1px solid ${colors.border}`,
                background: 'transparent', color: colors.textMid, cursor: 'pointer',
                fontSize: '0.85rem', fontFamily: 'inherit',
              }}
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleAdd}
              disabled={saving}
              style={{
                padding: '8px 16px', borderRadius: 6, border: 'none',
                background: '#2563eb', color: '#fff', cursor: saving ? 'wait' : 'pointer',
                fontSize: '0.85rem', fontFamily: 'inherit', fontWeight: 600,
                opacity: saving ? 0.5 : 1,
              }}
            >
              {saving ? 'Ajout...' : 'Ajouter'}
            </button>
          </div>
        </div>
      )}
    </ModalOverlay>
  );
}
